// Pure rules behind the drawer's team editor (kanban ae374351), kept out of
// the component file so fast refresh stays happy and the rules are reusable.

// Mirrors services/roadmap/contract.py `_MAX_TEAMS`.
export const MAX_TEAMS = 6;

/** Trim + collapse whitespace, then add unless already present
 * case-insensitively (first spelling wins) — the contract's normalisation. */
export function addTeam(list: string[], raw: string): string[] {
  const name = raw.split(/\s+/).filter(Boolean).join(' ');
  if (!name) return list;
  if (list.some((t) => t.toLowerCase() === name.toLowerCase())) return list;
  return [...list, name];
}

export function sameTeamList(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((t, n) => t === b[n]);
}

/** v9: what the editor saves — one owning team (null = no task force) and the
 * related ones. Mirrors services/roadmap/contract.py `TeamAssignment`. */
export interface TeamAssignmentDraft {
  owner: string | null;
  related: string[];
  /** v10: the owner's mandate area (null = let Merlin file it). Absent on
   * drafts that never carried one. */
  area?: string | null;
}

/** Fold a typed owner like a chip: trimmed, collapsed whitespace, blank -> null. */
export function cleanOwner(raw: string | null | undefined): string | null {
  const name = (raw ?? '').split(/\s+/).filter(Boolean).join(' ');
  return name || null;
}

/** A team cannot both own an item and be merely related to it: naming it as
 * the owner drops it from the related list (case-insensitively). */
export function withOwner(draft: TeamAssignmentDraft, raw: string | null): TeamAssignmentDraft {
  const owner = cleanOwner(raw);
  const related = owner ? draft.related.filter((t) => t.toLowerCase() !== owner.toLowerCase()) : draft.related;
  const out: TeamAssignmentDraft = { owner, related };
  // A mandate area belongs to a team: a new owner starts unfiled.
  if (draft.area !== undefined) {
    const same = (owner ?? '').toLowerCase() === (draft.owner ?? '').toLowerCase();
    out.area = same ? draft.area : null;
  }
  return out;
}

/** File the draft under one of the owner's areas (blank = let Merlin file it). */
export function withArea(draft: TeamAssignmentDraft, area: string | null): TeamAssignmentDraft {
  return { ...draft, area: area || null };
}

/** Add a related team unless it is the owner or already listed. */
export function addRelated(draft: TeamAssignmentDraft, raw: string): TeamAssignmentDraft {
  const name = cleanOwner(raw);
  if (!name) return draft;
  if (draft.owner && draft.owner.toLowerCase() === name.toLowerCase()) return draft;
  return { ...draft, related: addTeam(draft.related, name) };
}

/** Owner plus related: the backend's `teams` limit counts both. */
export function assignmentSize(draft: TeamAssignmentDraft): number {
  return draft.related.length + (draft.owner ? 1 : 0);
}

export function sameAssignment(a: TeamAssignmentDraft, b: TeamAssignmentDraft): boolean {
  return (a.owner ?? null) === (b.owner ?? null) && sameTeamList(a.related, b.related) && (a.area ?? null) === (b.area ?? null);
}
